import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import ComboboxContext from './ComboboxContext';
import useViewportMenuMaxHeight from './useViewportMenuMaxHeight';
import './Combobox.scss';

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

const defaultFilterFn = (label, searchTerm) => label.toLowerCase().includes(searchTerm.toLowerCase());

function buildRegistry(children) {
  const registry = new Map();

  React.Children.forEach(children, child => {
    if (!child || !child.props) return;
    if (child.props.value !== undefined && child.props.label) {
      registry.set(child.props.value, child.props.label);
    }
    if (child.props.children) {
      const nested = buildRegistry(child.props.children);
      nested.forEach((label, val) => registry.set(val, label));
    }
  });

  return registry;
}

const Combobox = ({
  value,
  onChange,
  multiple = false,
  filterFn = defaultFilterFn,
  closeOnSelect,
  autoFocus = false,
  invalid = false,
  describedBy,
  menuBottomOffset = 0,
  menuViewportMargin = 0,
  menuMinHeight = 0,
  onOpenChange,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const itemValuesRef = useRef([]);

  const menuMaxHeight = useViewportMenuMaxHeight(wrapperRef, isOpen, {
    bottomOffset: menuBottomOffset,
    viewportMargin: menuViewportMargin,
    minMenuHeight: menuMinHeight,
  });

  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (wasOpenRef.current === isOpen) return;
    wasOpenRef.current = isOpen;
    if (onOpenChange) onOpenChange(isOpen);
  }, [isOpen, onOpenChange]);

  const shouldCloseOnSelect = closeOnSelect !== undefined ? closeOnSelect : !multiple;

  const registry = useMemo(() => buildRegistry(children), [children]);

  const getLabelForValue = useCallback(val => registry.get(val) || '', [registry]);

  const inputValue = useMemo(() => {
    if (isOpen) return searchTerm;
    if (!multiple && value) return getLabelForValue(value);
    return '';
  }, [isOpen, searchTerm, multiple, value, getLabelForValue]);

  const onInputChange = useCallback(text => {
    setSearchTerm(text);
    setHighlightedIndex(-1);
  }, []);

  const openDropdown = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      setSearchTerm('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  }, []);

  const isSelected = useCallback(
    itemValue => {
      if (multiple) return Array.isArray(value) && value.includes(itemValue);
      return value === itemValue;
    },
    [multiple, value]
  );

  const selectItem = useCallback(
    itemValue => {
      if (multiple) {
        const currentValues = Array.isArray(value) ? value : [];
        const newValues = currentValues.includes(itemValue)
          ? currentValues.filter(v => v !== itemValue)
          : [...currentValues, itemValue];
        onChange(newValues);
      } else {
        onChange(itemValue);
      }

      if (shouldCloseOnSelect) {
        closeDropdown();
      } else if (isTouchDevice()) {
        // Touch keyboards cover the menu; blur the input so the user can see
        // what they just selected. Tapping the input reopens the keyboard.
        inputRef.current?.blur();
      }
    },
    [multiple, value, onChange, shouldCloseOnSelect, closeDropdown]
  );

  const filteredChildren = useCallback(
    listChildren => {
      const childArray = React.Children.toArray(listChildren);
      const filteredValues = [];

      const filtered = childArray.filter(child => {
        if (!child || !child.props) return false;
        if (!child.props.label) return true;
        if (!searchTerm) {
          filteredValues.push(child.props.value);
          return true;
        }
        const matches = filterFn(child.props.label, searchTerm);
        if (matches) filteredValues.push(child.props.value);
        return matches;
      });

      itemValuesRef.current = filteredValues;
      return filtered;
    },
    [searchTerm, filterFn]
  );

  const hasFilteredResults = useMemo(() => {
    if (!isOpen) return true;
    const allValues = Array.from(registry.keys());
    if (!searchTerm) return allValues.length > 0;
    return allValues.some(val => filterFn(registry.get(val), searchTerm));
  }, [isOpen, searchTerm, registry, filterFn]);

  const getItemIndex = useCallback(itemValue => itemValuesRef.current.indexOf(itemValue), []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      setIsOpen(true);
    }
  }, [autoFocus]);

  useEffect(() => {
    const handleMouseDown = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [closeDropdown]);

  const handleKeyDown = useCallback(
    e => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          openDropdown();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setHighlightedIndex(prev => {
            const vals = itemValuesRef.current;
            const next = prev + 1;
            return next >= vals.length ? 0 : next;
          });
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setHighlightedIndex(prev => {
            const vals = itemValuesRef.current;
            const next = prev - 1;
            return next < 0 ? vals.length - 1 : next;
          });
          break;
        }
        case 'Enter': {
          e.preventDefault();
          const vals = itemValuesRef.current;
          if (highlightedIndex >= 0 && highlightedIndex < vals.length) {
            selectItem(vals[highlightedIndex]);
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          closeDropdown();
          break;
        }
        default:
          break;
      }
    },
    [isOpen, highlightedIndex, selectItem, closeDropdown, openDropdown]
  );

  const contextValue = useMemo(
    () => ({
      isOpen,
      searchTerm,
      inputRef,
      inputValue,
      onInputChange,
      onKeyDown: handleKeyDown,
      openDropdown,
      closeDropdown,
      isSelected,
      selectItem,
      highlightedIndex,
      getItemIndex,
      multiple,
      filteredChildren,
      hasFilteredResults,
      menuMaxHeight,
      invalid,
      describedBy,
    }),
    [
      isOpen,
      searchTerm,
      inputValue,
      onInputChange,
      handleKeyDown,
      openDropdown,
      closeDropdown,
      isSelected,
      selectItem,
      highlightedIndex,
      getItemIndex,
      multiple,
      filteredChildren,
      hasFilteredResults,
      menuMaxHeight,
      invalid,
      describedBy,
    ]
  );

  return (
    <ComboboxContext.Provider value={contextValue}>
      <div ref={wrapperRef} className="combobox">
        {children}
      </div>
    </ComboboxContext.Provider>
  );
};

Combobox.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  filterFn: PropTypes.func,
  closeOnSelect: PropTypes.bool,
  autoFocus: PropTypes.bool,
  describedBy: PropTypes.string,
  invalid: PropTypes.bool,
  menuBottomOffset: PropTypes.number,
  menuViewportMargin: PropTypes.number,
  menuMinHeight: PropTypes.number,
  onOpenChange: PropTypes.func,
  children: PropTypes.node,
};

export default Combobox;
